import { getCookieConsent } from '../contexts/CookieConsentContext';
import { isReturningVisitor, getHoursSinceLastActivity, setLastActivityTimestamp, getLastActivityTimestamp } from '../lib/returningVisitorService';

// Lazy-load client-only modules to avoid SSR failures
let _ReactGA = null;
const getReactGA = () => {
  if (!_ReactGA && typeof window !== 'undefined') {
    _ReactGA = require('react-ga4').default;
  }
  return _ReactGA;
};

let _Cookies = null;
const getCookies = () => {
  if (!_Cookies && typeof window !== 'undefined') {
    _Cookies = require('js-cookie');
  }
  return _Cookies;
};

// Send events to our database (not just GA4)
const sendToDatabaseAnalytics = async (eventName, properties) => {
  try {
    // /api/analytics/event is authenticated and has no anonymous mode, so
    // posting without a token only produced a 401 and a console error on every
    // logged-out interaction. Anonymous events still reach GA4/PostHog.
    const token = getCookies()?.get('token');
    if (!token) {
      return;
    }

    const eventData = {
      eventName,
      properties: {
        ...properties,
        page: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
        timestamp: new Date().toISOString()
      }
    };

    await fetch('/api/analytics/event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(eventData)
    });
  } catch (error) {
    console.error('[Analytics] Failed to send to database:', error);
  }
};

// Analytics Properties Configuration
const ANALYTICS_PROPERTIES = {
  // Development Environment
  dev_public: {
    tag: 'G-6VDQWYF2MC',
    id: 518003720
  },
  dev_logged_in: {
    tag: 'G-PX1SH27CZD', 
    id: 520313125
  },
  // Production Environment
  live_public: {
    tag: 'G-1GEY2ZD31E',
    id: 520260631
  },
  live_logged_in: {
    tag: 'G-M3KFF62NZG',
    id: 520307157
  }
};

let currentPropertyTag = null;

// Runtime hostname guard. Even when NODE_ENV === 'production' (e.g., someone
// runs `next start` against a local build), a browser served from localhost
// must not write to live_public - that pollutes the production property.
// Per DEL-41: live_public (520260631) was 68% localhost contamination before
// this guard. Production deployments go to delphina.money, not localhost.
const isLocalHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host === '::1';
};

// Get current environment and user authentication state
export const getAnalyticsProperty = () => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  // Use js-cookie to check for the token as AuthContext sets it in cookies
  const Cookies = getCookies();
  const token = Cookies?.get('token');
  const isLoggedIn = !!token && token !== 'null' && token !== 'undefined';

  // Hostname guard takes precedence over NODE_ENV. A `next start` local
  // session still gets NODE_ENV='production' from Next.js, but the browser
  // is on localhost and must route to the dev property bucket.
  const forceDev = isDevelopment || isLocalHost();

  if (forceDev) {
    return isLoggedIn ? ANALYTICS_PROPERTIES.dev_logged_in : ANALYTICS_PROPERTIES.dev_public;
  } else {
    return isLoggedIn ? ANALYTICS_PROPERTIES.live_logged_in : ANALYTICS_PROPERTIES.live_public;
  }
};

// UTM parameter tracking
const getUTMParameters = () => {
  if (typeof window === 'undefined') return {};
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content'),
  };
};

export const initializeAnalytics = () => {
  const property = getAnalyticsProperty();
  const measurementId = property.tag;
  console.log('[GA4] Initializing with ID:', measurementId);

  // If we are already initialized with the same ID, do nothing
  if (currentPropertyTag === measurementId) {
    console.log('[GA4] Already initialized with same ID, skipping');
    return;
  }

  const ReactGA = getReactGA();
  console.log('[GA4] ReactGA loaded:', !!ReactGA, ReactGA);
  if (!ReactGA) {
    console.error('[GA4] ReactGA failed to load!');
    return;
  }

  try {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        cookieFlags: 'SameSite=Lax; Secure',
        anonymize_ip: true,
        send_page_view: false // We track page views manually
      },
    });
    console.log('[GA4] initialize() called successfully');
  } catch (e) {
    console.error('[GA4] initialize() failed:', e);
  }

  currentPropertyTag = measurementId;
  
  // Track UTM parameters on initialization
  const utmParams = getUTMParameters();
  const hasUTM = Object.values(utmParams).some(param => param !== null);
  
  if (hasUTM) {
    // Store UTM parameters for user session
    if (typeof window !== 'undefined') {
      localStorage.setItem('utm_parameters', JSON.stringify(utmParams));
    }
    getReactGA()?.event('utm_source_tracking', utmParams);
  }
};

// Helper to check if we need to switch properties (e.g. after login)
export const checkAndReloadAnalytics = () => {
  const expectedProperty = getAnalyticsProperty();
  if (currentPropertyTag && currentPropertyTag !== expectedProperty.tag) {
    console.log('Analytics property mismatch. Re-initializing...');
    initializeAnalytics();
    
    // Optionally force reload if we want absolutely clean state
    // window.location.reload(); 
    // For now, re-initializing sets the new ID as the default for subsequent calls.
  }
};

export const trackPageView = (path, title) => {
  console.log('[GA4] trackPageView called:', path, title);

  // Check consent before tracking
  if (!getCookieConsent('analytics')) {
    console.log('[GA4] Page view blocked due to lack of consent');
    return;
  }

  const ReactGA = getReactGA();
  console.log('[GA4] ReactGA in trackPageView:', !!ReactGA);
  if (!ReactGA) {
    console.error('[GA4] ReactGA not loaded!');
    return;
  }

  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: title,
  });
  console.log('[GA4] Page view sent');

  // Write to database and check for returning visitor (non-blocking)
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      // Send page_view to our database
      sendToDatabaseAnalytics('page_view', { path, title });

      // Check for returning visitor
      const isReturning = isReturningVisitor();
      const lastActivity = getLastActivityTimestamp();
      const now = Date.now();
      console.log('[ReturningVisitor] check:', {
        lastActivity,
        now,
        diffMs: lastActivity ? now - lastActivity : null,
        thresholdMs: 2 * 60 * 60 * 1000,
        isReturning
      });

      if (isReturning) {
        const hours = getHoursSinceLastActivity();
        setLastActivityTimestamp();

        const token = getCookies().get('token');
        if (token) {
          fetch('/api/analytics/returning-visitor', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ hoursSinceLastActivity: hours })
          }).catch(err => console.warn('[ReturningVisitor] Notification failed:', err));
        }
      } else {
        setLastActivityTimestamp();
      }
    }, 0);
  }
};

export const trackEvent = (eventName, parameters = {}) => {
  console.log('[GA4] trackEvent called:', eventName, parameters);

  // Check consent before tracking
  if (!getCookieConsent('analytics')) {
    console.log(`[GA4] Event "${eventName}" blocked due to lack of consent`);
    return;
  }

  const ReactGA = getReactGA();
  if (!ReactGA) {
    console.error('[GA4] ReactGA not loaded in trackEvent!');
    return;
  }

  ReactGA.event(eventName, parameters);
  console.log('[GA4] Event sent:', eventName);
};

export const setUserId = (userId) => {
  getReactGA()?.set({ userId });
};

export const setUserProperties = (properties) => {
  getReactGA()?.gtag('set', 'user_properties', properties);
};

export const trackPurchase = (planName, price, transactionId) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: price,
    currency: 'GBP',
    items: [{
      item_id: planName,
      item_name: planName,
      item_category: 'Subscription',
      price,
      quantity: 1
    }]
  });
};

export const trackCalcStarted = () => {
  trackEvent('calc_started', { source: 'landlord_tax_calculator' });
};

export const trackCalcInputChanged = () => {
  trackEvent('calc_input_changed', { source: 'landlord_tax_calculator' });
};

export const trackCalcCompleted = (formData) => {
  trackEvent('calc_completed', {
    source: 'landlord_tax_calculator',
    property_type: formData?.propertyType,
    tax_band: formData?.taxBand
  });
};

export const trackCalcShared = () => {
  trackEvent('calc_shared', { source: 'landlord_tax_calculator' });
};

export const trackCalcReportRequested = () => {
  trackEvent('calc_report_requested', { source: 'landlord_tax_calculator' });
};

// Phase 1: Core Events
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', { method });
};

export const trackDashboardFirstView = () => {
  trackEvent('dashboard_first_view');
};

export const trackActivationMilestone = () => {
  trackEvent('activation_milestone_reached');
};

export const trackFirstAssetAdded = (assetType, category) => {
  trackEvent('first_asset_added', { asset_type: assetType, category });
};

// Income & Expense Tracking
export const trackAddIncome = (incomeType, category, frequency) => {
  trackEvent('add_income', { income_type: incomeType, category, frequency });
};

export const trackAddExpense = (expenseType, category, frequency) => {
  trackEvent('add_expense', { expense_type: expenseType, category, frequency });
};

export const trackFirstIncomeAdded = (incomeType, category) => {
  const hasAddedIncome = localStorage.getItem('has_added_income');
  if (!hasAddedIncome) {
    trackEvent('first_income_added', { income_type: incomeType, category });
    localStorage.setItem('has_added_income', 'true');
  }
};

export const trackFirstExpenseAdded = (expenseType, category) => {
  const hasAddedExpense = localStorage.getItem('has_added_expense');
  if (!hasAddedExpense) {
    trackEvent('first_expense_added', { expense_type: expenseType, category });
    localStorage.setItem('has_added_expense', 'true');
  }
};

// Transaction Tracking
export const trackAddTransaction = (transactionType, category) => {
  trackEvent('add_transaction', { transaction_type: transactionType, category });
};

export const trackUploadTransactionCSV = (fileSize, transactionCount) => {
  trackEvent('upload_transaction_csv', { file_size: fileSize, transaction_count: transactionCount });
};

export const trackUploadTransactionPDF = (fileSize, transactionCount) => {
  trackEvent('upload_transaction_pdf', { file_size: fileSize, transaction_count: transactionCount });
};

// Portfolio Management Events
export const trackAddPortfolio = (portfolioName) => {
  trackEvent('add_portfolio', { portfolio_name: portfolioName });
};

export const trackPortfolioSettingsOpen = (settingsType) => {
  trackEvent('portfolio_settings_open', { settings_type: settingsType });
};

export const trackEditAssetCategories = () => {
  trackEvent('edit_asset_categories');
};

export const trackEditIncomeCategories = () => {
  trackEvent('edit_income_categories');
};

export const trackEditExpenseCategories = () => {
  trackEvent('edit_expense_categories');
};

// UK-Specific Events
export const trackUKPensionCalculatorUsage = (calculatorType) => {
  trackEvent('uk_pension_calculator_usage', { calculator_type: calculatorType });
};

export const trackUKTaxYearPlanning = (planningAction) => {
  trackEvent('uk_tax_year_planning', { planning_action: planningAction });
};

export const trackFinancialConfidenceScore = (scoreBefore, scoreAfter) => {
  trackEvent('financial_confidence_score', { 
    score_before: scoreBefore, 
    score_after: scoreAfter,
    confidence_change: scoreAfter - scoreBefore
  });
};

export const trackRetirementPlanningStart = (planningType) => {
  trackEvent('retirement_planning_start', { planning_type: planningType });
};

export const trackPortfolioStressTest = (stressType) => {
  trackEvent('portfolio_stress_test', { stress_type: stressType });
};

export const trackScenarioModification = (scenarioType) => {
  trackEvent('scenario_modification', { scenario_type: scenarioType });
};

export const trackUpgradeTriggerEvent = (triggerType) => {
  trackEvent('upgrade_trigger_event', { trigger_type: triggerType });
};

export const trackTimeToFirstValue = (timeInMinutes) => {
  trackEvent('time_to_first_value', { time_to_value_minutes: timeInMinutes });
};

export const trackWeeklyActiveFeatures = (features) => {
  trackEvent('weekly_active_features', { features_used: features });
};

export const trackFeatureToUpgradeCorrelation = (feature, timeToUpgrade) => {
  trackEvent('feature_to_upgrade_correlation', { 
    feature, 
    time_to_upgrade_hours: timeToUpgrade 
  });
};

// Weekly active features tracking
export const trackWeeklyFeature = (featureName) => {
  const weekKey = `features_${getCurrentWeek()}`;
  const weeklyFeatures = JSON.parse(localStorage.getItem(weekKey) || '[]');
  
  if (!weeklyFeatures.includes(featureName)) {
    weeklyFeatures.push(featureName);
    localStorage.setItem(weekKey, JSON.stringify(weeklyFeatures));
    
    // Track when we hit 3, 5, 7+ features (engagement milestones)
    if (weeklyFeatures.length === 3 || weeklyFeatures.length === 5 || weeklyFeatures.length === 7) {
      trackEvent('weekly_active_features', { 
        features_used: weeklyFeatures.length,
        feature_list: weeklyFeatures
      });
    }
  }
};

const getCurrentWeek = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diff = now - start;
  const oneWeek = 1000 * 60 * 60 * 24 * 7;
  return Math.floor(diff / oneWeek);
};

// Helper function to track user interactions
export const trackUserInteraction = (action, category, label) => {
  trackEvent('user_interaction', {
    action,
    category,
    label,
  });
};

// Referral Program Analytics
export const trackReferralCodeViewed = (referralCode) => {
  trackEvent('referral_code_viewed', { referral_code: referralCode });
};

export const trackReferralLinkShared = (referralCode, channel) => {
  trackEvent('referral_link_shared', {
    referral_code: referralCode,
    channel, // 'twitter', 'facebook', 'email', 'copy'
  });
};

export const trackReferralSignupStarted = (referralCode, source) => {
  trackEvent('referral_signup_started', {
    referral_code: referralCode,
    source: source || 'direct',
  });

  // Also send to our database analytics
  sendToDatabaseAnalytics('referral_signup_started', {
    referral_code: referralCode,
    source: source || 'direct',
  });
};

export const trackReferralSignupCompleted = (referralCode, userId) => {
  trackEvent('referral_signup_completed', {
    referral_code: referralCode,
    user_id: userId,
  });
};

export const trackReferralRewardTriggered = (referrerId, referredUserId, rewardMonths) => {
  trackEvent('referral_reward_triggered', {
    referrer_id: referrerId,
    referred_user_id: referredUserId,
    reward_months: rewardMonths,
  });
};

export const trackReferralUpgradeReward = (referrerId, referredUserId, tier) => {
  trackEvent('referral_upgrade_reward', {
    referrer_id: referrerId,
    referred_user_id: referredUserId,
    tier,
  });
};
